// Port-VLAN mismatch detection + resolution for a connection (Phase 16).
//
// When a connection has BOTH endpoint ports assigned, compare the resolved
// VLAN configuration (untagged/access VLAN + tagged/trunk VLANs, honouring
// config groups) of the two ports. If they differ we surface a resolution
// dialog letting the user adopt one side's config onto the other.
//
// Pure JS (also imported by the Node round-trip test) — no DOM/React here.
import { findDeviceGlobal, getAllEdges } from './detail';
import { resolvePortVlans } from './vlanPorts';
import { deviceLabel, isPassiveDevice } from './deviceIcons';

// Detail-view port ids carry a side suffix (`__l` / `__r`); the underlying
// config lives on the base port id, so strip it before resolving.
function basePortId(pid) {
  return typeof pid === 'string' ? pid.replace(/__[lr]$/, '') : pid;
}

function sameRefs(a, b) {
  const sa = [...(a || [])].sort();
  const sb = [...(b || [])].sort();
  return sa.length === sb.length && sa.every((x, i) => x === sb[i]);
}

// Follow a PASSIVE device (patch panel) through to the nearest ACTIVE device
// port. Passive devices merely carry a connection through, so the effective
// VLAN config of one of their ports is that of the active peer on the far side.
// We look at the passive device's port, find the OTHER edge on the SAME port
// (excluding the edge we came in on) and recurse until an active device port is
// reached. Chained passive panels are supported; `seen` guards against loops.
// Returns { deviceId, portId, node } of the active far port, or null.
export function resolvePassthrough(doc, deviceId, portId, excludeEdgeId, seen = new Set()) {
  const node = findDeviceGlobal(doc, deviceId);
  if (!node) return null;
  const pid = basePortId(portId);
  if (!isPassiveDevice(node.data?.deviceType)) {
    return { deviceId: node.id, portId: pid, node };
  }
  const key = node.id + ':' + pid;
  if (seen.has(key)) return null;
  seen.add(key);
  for (const e of getAllEdges(doc)) {
    if (e.id === excludeEdgeId) continue;
    let peerDev = null;
    let peerPort = null;
    if ((e.source === deviceId || e.source === node.id) && basePortId(e.sourcePortId) === pid) {
      peerDev = e.target;
      peerPort = e.targetPortId;
    } else if ((e.target === deviceId || e.target === node.id) && basePortId(e.targetPortId) === pid) {
      peerDev = e.source;
      peerPort = e.sourcePortId;
    }
    if (peerDev && peerPort != null) {
      const r = resolvePassthrough(doc, peerDev, peerPort, e.id, seen);
      if (r) return r;
    }
  }
  return null;
}

// Resolve the full VLAN/display info for an ACTIVE device port.
function buildResolvedInfo(doc, node, pid, lang) {
  const resolved = resolvePortVlans(node, doc)[pid];
  if (!resolved) return null;
  const port = (node.data?.ports || []).find((p) => p.id === pid);
  return {
    deviceId: node.id,
    portId: pid,
    deviceName: node.data?.label || deviceLabel(node.data?.deviceType, lang),
    portName: port?.name || pid,
    groupName: resolved.groupName || null,
    untaggedRef: resolved.untaggedVlan ? resolved.untaggedVlan.id : null,
    untaggedVlan: resolved.untaggedVlan || null,
    taggedRefs: (resolved.taggedVlans || []).map((v) => v.id),
    taggedVlans: resolved.taggedVlans || [],
  };
}

function sideInfo(doc, deviceId, portId, lang, excludeEdgeId) {
  const node = findDeviceGlobal(doc, deviceId);
  if (!node) return null;
  const pid = basePortId(portId);

  // Passive device (patch panel): resolve the effective config from the nearest
  // ACTIVE device on the far side of the panel (pass-through). Writing (adopt)
  // therefore targets that far active port, not the passive panel itself.
  if (isPassiveDevice(node.data?.deviceType)) {
    const far = resolvePassthrough(doc, node.id, pid, excludeEdgeId, new Set());
    const passivePort = (node.data?.ports || []).find((p) => p.id === pid);
    const passiveName = node.data?.label || deviceLabel(node.data?.deviceType, lang);
    if (!far || !far.node) {
      // No active peer through the panel yet -> nothing to compare / adopt.
      return {
        deviceId: node.id,
        portId: pid,
        deviceName: passiveName,
        portName: passivePort?.name || pid,
        passive: true,
        empty: true,
        groupName: null,
        untaggedRef: null,
        untaggedVlan: null,
        taggedRefs: [],
        taggedVlans: [],
      };
    }
    const info = buildResolvedInfo(doc, far.node, far.portId, lang);
    if (!info) return null;
    return {
      ...info,
      passive: true,
      // The panel port the user actually assigned (for reference/labels).
      panelDeviceId: node.id,
      panelPortId: pid,
      panelDeviceName: passiveName,
      panelPortName: passivePort?.name || pid,
      passthrough: { deviceName: info.deviceName, portName: info.portName },
    };
  }

  return buildResolvedInfo(doc, node, pid, lang);
}

// Returns null when either port is unassigned OR the two configs are identical
// OR a passive side has no active peer to pass through; otherwise returns
// { edgeId, a, b } with full display + raw-ref info.
export function edgeVlanMismatch(doc, edge, lang = 'de') {
  if (!edge || !edge.sourcePortId || !edge.targetPortId) return null;
  const a = sideInfo(doc, edge.source, edge.sourcePortId, lang, edge.id);
  const b = sideInfo(doc, edge.target, edge.targetPortId, lang, edge.id);
  if (!a || !b) return null;
  if (a.empty || b.empty) return null; // passive side without an active far peer
  const sameUntagged = a.untaggedRef === b.untaggedRef;
  const sameTagged = sameRefs(a.taggedRefs, b.taggedRefs);
  if (sameUntagged && sameTagged) return null;
  return { edgeId: edge.id, a, b };
}

// Apply a VLAN config { untaggedVlanRef, taggedVlanRefs } to a device port.
// Group-aware: if the port belongs to a config group, the group is updated
// (it overrides the individual port); otherwise the port itself is updated.
export function applyPortVlanConfig(doc, deviceId, portId, cfg) {
  const pid = basePortId(portId);
  const untaggedVlanRef = cfg.untaggedVlanRef || null;
  const taggedVlanRefs = [...(cfg.taggedVlanRefs || [])];
  return {
    ...doc,
    pages: (doc.pages || []).map((p) => ({
      ...p,
      nodes: (p.nodes || []).map((n) => {
        if (n.id !== deviceId) return n;
        const data = { ...n.data };
        const grp = (data.portGroups || []).find((g) => (g.memberIds || []).includes(pid));
        if (grp) {
          data.portGroups = data.portGroups.map((g) =>
            g.id === grp.id ? { ...g, untaggedVlanRef, taggedVlanRefs: [...taggedVlanRefs] } : g
          );
        } else {
          data.ports = (data.ports || []).map((pt) =>
            pt.id === pid ? { ...pt, untaggedVlanRef, taggedVlanRefs: [...taggedVlanRefs] } : pt
          );
        }
        return { ...n, data };
      }),
    })),
  };
}
